import styled from "@emotion/native"
import { StackNavigationProp } from "@react-navigation/stack"
import Cosmos from "../assets/onboarding-v2/cosmos.svg"
import Logo from "../assets/onboarding-v2/logo.svg"
import { Text } from "../ui-components/Typography"
import { Spacing } from "../ui-components/Spacing"
import { ButtonV2 } from "../ui-components/ButtonV2"
import { RootStackParamList } from "../router/types"
import { useQueryClient } from "@tanstack/react-query"
import { useFocusEffect, useIsFocused } from "@react-navigation/native"
import { useCallback, useEffect, useState } from "react"
import { Dimensions, Platform, ScrollView, View } from "react-native"
import { retrieveValue, shouldAllowLogIn } from "../utils"
import { LayoutChangeEvent } from "react-native"
import { SafeAreaView } from "../ui-components/SafeAreaView"
import { useFeatureFlag } from "../hooks"

type InitialScreenNavigationProp = StackNavigationProp<RootStackParamList, "Initial">

type InitialScreen = {
  navigation: InitialScreenNavigationProp
}

export function InitialScreenV2({ navigation }: InitialScreen) {
  const queryClient = useQueryClient()
  const [showButtons, setShowButtons] = useState(true)
  const isFocused = useIsFocused()
  const [viewHeights, setViewHeights] = useState([0, 0, 0])
  const unifiedAuthEnabled = useFeatureFlag("unified-auth-entry") === true

  const handleLayout = (event: LayoutChangeEvent, index: number) => {
    const { height } = event.nativeEvent.layout
    setViewHeights((curr) => {
      const copy = [...curr]
      copy[index] = Math.floor(height)
      return copy
    })
  }
  const dimensions = Dimensions.get("screen")

  const SVG_MAX_WIDTH = Math.min(350, dimensions.width)
  const SVG_MAX_HEIGHT = SVG_MAX_WIDTH / 1.55

  let cosmosSvgHeight =
    dimensions.height -
    viewHeights.reduce((acc, curr) => acc + curr, 0) -
    (Platform.OS === "web" ? 0 : 84)
  let cosmosSvgWidth = cosmosSvgHeight * 1.55

  if (cosmosSvgHeight > SVG_MAX_HEIGHT || cosmosSvgHeight <= 0) {
    cosmosSvgHeight = SVG_MAX_HEIGHT
  }
  if (cosmosSvgWidth > SVG_MAX_WIDTH || cosmosSvgWidth <= 0) {
    cosmosSvgWidth = SVG_MAX_WIDTH
  }

  useFocusEffect(
    useCallback(() => {
      queryClient.clear()
      queryClient.setQueryData(["BOOT_TIMESTAMP"], performance.now())
    }, [queryClient])
  )

  useEffect(() => {
    if (Platform.OS === "web") {
      return
    }
    retrieveValue("isLoginDataStored").then((isLogged) => {
      if (isLogged === true.toString()) {
        // Always LogIn: it runs biometrics + refresh-token exchange. SignUpFlow
        // would force a fresh phone + OTP even when a session is already stored.
        navigation.navigate("LogIn")
      } else {
        setShowButtons(true)
      }
    })
  }, [navigation])

  useEffect(() => {
    if (!isFocused) {
      setShowButtons(true)
    }
  }, [isFocused])

  const navigateToSignUpFlow = () => {
    navigation.navigate("SignUpFlow")
  }
  const navigateToLogIn = () => {
    navigation.navigate("LogIn")
  }

  return (
    <Wrapper>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: 20,
        }}
        alwaysBounceVertical={false}
      >
        <UpperContent>
          <View onLayout={(e) => handleLayout(e, 0)}>
            <Spacing top={20} />
            <Logo />
            <Spacing top={40} />
          </View>
          <View
            style={{
              maxWidth: SVG_MAX_WIDTH,
              maxHeight: SVG_MAX_HEIGHT,
              width: cosmosSvgWidth,
              height: cosmosSvgHeight,
            }}
          >
            <Cosmos />
          </View>
          <View onLayout={(e) => handleLayout(e, 1)}>
            <Spacing top={50} />
            <Text
              fontSize={36}
              lineHeight={43.2}
              color="white"
              fontFamily="IBMPlexSans-SemiBold"
              align="center"
            >
              The New Way{"\n"}to Pay
            </Text>
            <Spacing top={24} />
            <Text
              fontSize={20}
              lineHeight={32}
              color="white"
              align="center"
              fontFamily="IBMPlexSans-Regular"
            >
              Simplify your financial life with a centralized billing inbox, one-tap payments, and
              easy recordkeeping.
            </Text>
          </View>
        </UpperContent>
        <ButtonsContainer isVisible={showButtons} onLayout={(e) => handleLayout(e, 2)}>
          {unifiedAuthEnabled ? (
            <ButtonV2
              title="Continue"
              testID="continue"
              accessibilityLabel="Continue"
              onPress={navigateToSignUpFlow}
              variant="blue2"
            />
          ) : (
            <>
              <ButtonV2
                title="Create account"
                testID="create-account"
                accessibilityLabel="Create account"
                onPress={navigateToSignUpFlow}
                variant="blue2"
              />
              {shouldAllowLogIn() && (
                <>
                  <Spacing top={16} />
                  <ButtonV2 variant="white" onPress={navigateToLogIn} title="Log In" />
                </>
              )}
            </>
          )}
        </ButtonsContainer>
      </ScrollView>
    </Wrapper>
  )
}

const Wrapper = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.v2.screens.initial.backgroundColor};
`

const UpperContent = styled.View`
  align-items: center;
`

const ButtonsContainer = styled.View<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
  width: 100%;
  max-width: 500px;
  padding-top: 23px;
  padding-bottom: ${() => (Platform.OS === "web" ? "20px" : 0)};
`
